import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface InsuranceStats {
  recoverableDepreciation: number;
  activeSupplements: number;
  supplementsValue: number;
  avgClaimIncrease: number;
}

export const useInsuranceDashboardStats = (orgId?: string) => {
  const [stats, setStats] = useState<InsuranceStats>({
    recoverableDepreciation: 0,
    activeSupplements: 0,
    supplementsValue: 0,
    avgClaimIncrease: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Get all insurance jobs
        const { data: insuranceJobs } = await supabase
          .from('jobs')
          .select('id, property_type, estimated_value')
          .eq('org_id', orgId)
          .eq('property_type', 'Insurance');

        if (!insuranceJobs || insuranceJobs.length === 0) {
          setLoading(false);
          return;
        }

        const jobIds = insuranceJobs.map(j => j.id);

        // Get claim analyses for RCV/ACV (recoverable depreciation)
        const { data: claimAnalyses } = await supabase
          .from('claim_analyses')
          .select('rcv, acv')
          .in('job_id', jobIds);

        // Calculate recoverable depreciation (RCV - ACV)
        const recoverableDepreciation = claimAnalyses?.reduce((sum, claim) => {
          const rcv = claim.rcv || 0;
          const acv = claim.acv || 0;
          return sum + (rcv - acv);
        }, 0) || 0;

        // Get supplements
        const { data: supplements } = await supabase
          .from('supplements')
          .select('status, supplement_amount, original_claim_amount, approved_amount')
          .eq('org_id', orgId);

        // Count active supplements (pending, submitted, under_review)
        const activeSupplements = supplements?.filter(s => 
          ['pending', 'submitted', 'under_review'].includes(s.status || '')
        ).length || 0;

        // Sum active supplement values
        const supplementsValue = supplements?.filter(s => 
          ['pending', 'submitted', 'under_review'].includes(s.status || '')
        ).reduce((sum, s) => sum + (s.supplement_amount || 0), 0) || 0;

        // Calculate average claim increase from approved supplements
        // Using (approved_amount - original_claim_amount) / original_claim_amount * 100
        const approvedSupplements = supplements?.filter(s => 
          s.status === 'approved' && 
          s.original_claim_amount && 
          s.approved_amount
        ) || [];
        
        const avgClaimIncrease = approvedSupplements.length > 0
          ? approvedSupplements.reduce((sum, s) => {
              const original = s.original_claim_amount || 0;
              const approved = s.approved_amount || 0;
              const increase = original > 0 ? ((approved - original) / original) * 100 : 0;
              return sum + increase;
            }, 0) / approvedSupplements.length
          : 0;

        setStats({
          recoverableDepreciation: Math.round(recoverableDepreciation),
          activeSupplements,
          supplementsValue: Math.round(supplementsValue),
          avgClaimIncrease: Math.round(avgClaimIncrease),
        });
      } catch (error) {
        console.error('Error calculating insurance stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [orgId]);

  return { stats, loading };
};
